import { ReactNode, useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { EmptyState } from './empty-state'

interface Column<T> {
  header: string
  render: (row: T) => ReactNode
}

interface DataTableProps<T> {
  rows: T[]
  columns: Column<T>[]
  emptyTitle: string
  emptyDescription: string
  onRowClick?: (row: T) => void
  /** When set, enables pagination with the given page size as default. */
  pageSize?: number
  /** Page size options shown in the selector. */
  pageSizeOptions?: number[]
}

export function DataTable<T>({
  rows,
  columns,
  emptyTitle,
  emptyDescription,
  onRowClick,
  pageSize,
  pageSizeOptions = [10, 25, 50, 100],
}: DataTableProps<T>) {
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(pageSize ?? 10)

  // Reset to first page if row set shrinks past current page
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(rows.length / size))
    if (page >= totalPages) setPage(0)
  }, [rows.length, size, page])

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(rows.length / size)),
    [rows.length, size],
  )

  const paginated = useMemo(() => {
    if (!pageSize) return rows
    return rows.slice(page * size, page * size + size)
  }, [rows, page, size, pageSize])

  if (!rows.length) {
    return <EmptyState title={emptyTitle} description={emptyDescription} />
  }

  const startIdx = page * size + 1
  const endIdx = Math.min(rows.length, (page + 1) * size)

  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-[24px] border border-stone-800">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-stone-800">
            <thead className="bg-[#141414]">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.header}
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-stone-500"
                  >
                    {column.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-800 bg-[#1A1A1D]">
              {paginated.map((row, index) => (
                <tr
                  key={page * size + index}
                  className={cn(
                    'align-top',
                    onRowClick && 'cursor-pointer hover:bg-stone-800/50 transition',
                  )}
                  onClick={() => onRowClick?.(row)}
                >
                  {columns.map((column) => (
                    <td key={column.header} className="px-4 py-4 text-sm text-stone-300">
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {pageSize && rows.length > size && (
        <div className="flex flex-wrap items-center justify-between gap-3 px-1 text-xs text-stone-400">
          <div className="flex items-center gap-2">
            <span>
              Showing <span className="font-semibold text-stone-200">{startIdx}–{endIdx}</span> of{' '}
              <span className="font-semibold text-stone-200">{rows.length}</span>
            </span>
            <span className="text-stone-600">·</span>
            <label className="flex items-center gap-1.5">
              Rows per page
              <select
                value={size}
                onChange={(e) => {
                  setSize(Number(e.target.value))
                  setPage(0)
                }}
                className="rounded-md border border-stone-700 bg-stone-900 px-2 py-1 text-xs text-stone-200 focus:border-orange-500 focus:outline-none"
              >
                {pageSizeOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-stone-700 bg-stone-900 text-stone-300 transition hover:bg-stone-800 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="px-3 font-medium text-stone-300">
              Page {page + 1} of {totalPages}
            </span>
            <button
              type="button"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-stone-700 bg-stone-900 text-stone-300 transition hover:bg-stone-800 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
